import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  updateDoc,
  orderBy,
  addDoc,
} from "firebase/firestore";
import "./css/Profile.css";
import PageWrapper from "../components/PageWrapper";
import ProfileTab from "./Profile/ProfileTab";
import UsersTab from "./Profile/UsersTab";
import ArticleTab from "./Profile/ArticleTab";
import ConversationsTab from "./Profile/ConversationsTab";
import ErrorBoundary from "../components/ErrorBoundary";
// import MissionsTab from "./Profile/MissionsTab";
import MissionForm from "./Profile/MissionForm";
// import CharacterStoryTab from "./Profile/CharacterStoryTab";
import CharacterStoryForm from "./Profile/CharacterStoryForm";
// import LocationsTab from "./Profile/LocationsTab";
import LocationForm from "./Profile/LocationForm";
import AssetsTab from "./Profile/WeponsVehicleForm";
import TipsTab from "./Profile/TipsTab";
import MusicRadioForm from "./Profile/MusicRadioForm";

import { Helmet } from "react-helmet";

const Profile = ({ user }) => {
  const [privacy, setPrivacy] = useState("public");
  const [posts, setPosts] = useState([]);
  const [activeTab, setActiveTab] = useState("profile");
  const [role, setRole] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [userData, setUserData] = useState(null);
const isSuperAdmin = role === "superadmin";
const isAdminRole = ["superadmin", "admin"].includes(role);
const canContribute = ["superadmin", "admin", "contributor"].includes(role);

  // Fetch user profile data
  useEffect(() => {
    if (!user) {
      console.warn("⚠️ No user object provided.");
      return;
    }

    console.log("🔍 Loading user profile for UID:", user.uid);

    const loadUserData = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          console.log("✅ User data loaded:", data);
          setUserData(data);
          setPrivacy(data.privacy || "public");
          setRole(data.role || "member");
        } else {
          console.warn("⚠️ User document not found.");
          setUserData({});
        }
      } catch (err) {
        console.error("🚫 Error loading user data:", err);
        if (err.code === "permission-denied") {
          console.error("⛔ Permission denied to read user profile.");
        }
      }
    };

    loadUserData();
  }, [user]);

  // Fetch all users for superadmin
  useEffect(() => {
    if (role === "superadmin") {
      console.log("🔐 Superadmin detected. Loading all users...");

      const fetchUsers = async () => {
        try {
          const snap = await getDocs(collection(db, "users"));
          const usersList = snap.docs.map((doc) => doc.data());
          console.log(`👥 Fetched ${usersList.length} users.`);
          setAllUsers(usersList);
        } catch (err) {
          console.error("🚫 Error fetching users list:", err);
          if (err.code === "permission-denied") {
            console.error("⛔ Permission denied to read users collection.");
          }
        }
      };

      fetchUsers();
    }
  }, [role]);

  // Fetch posts for logged-in user
  useEffect(() => {
    if (!user) return;

    console.log("🧾 Loading posts for user:", user.email);

    const loadPosts = async () => {
      try {
        const q = query(
          collection(db, "community_posts"),
          where("user", "==", user.email)
        );
        const snap = await getDocs(q);
        const postList = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        console.log(`📝 Fetched ${postList.length} posts.`);
        setPosts(postList);
      } catch (err) {
        console.error("🚫 Error loading community posts:", err);
        if (err.code === "permission-denied") {
          console.error("⛔ Permission denied to read community_posts.");
        }
      }
    };

    loadPosts();
  }, [user, role]);

  // Log active tab changes
  useEffect(() => {
    console.log("📂 Active tab changed:", activeTab);
  }, [activeTab]);

  return (
    <>
      <Helmet>
        <title>
          {userData?.displayName
            ? `${userData.displayName.replace(
                /[^\w\s\-@.]/gi,
                ""
              )}'s Profile | GTAFanPage.com`
            : "My Profile | GTAFanPage.com"}
        </title>
        <meta
          name="description"
          content="Manage your GTAFanPage.com profile, view messages, and contact the team. Customize your presence and engage with the GTA VI fan community."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <PageWrapper>
        <div className="profile-page">
          {/* Sidebar */}
          <div className="profile-sidebar">
            <button
              className={activeTab === "profile" ? "active" : ""}
              onClick={() => setActiveTab("profile")}
            >
              👤 Profile
            </button>

            {role === "superadmin" && (
              <button
                className={activeTab === "contacts" ? "active" : ""}
                onClick={() => setActiveTab("contacts")}
              >
                📩 Contact
              </button>
            )}

            {user && role !== "superadmin" && (
              <button
                className={activeTab === "messages" ? "active" : ""}
                onClick={() => setActiveTab("messages")}
              >
                💬 Messages
              </button>
            )}

            {role === "superadmin" && (
              <button
                className={activeTab === "users" ? "active" : ""}
                onClick={() => setActiveTab("users")}
              >
                👥 Users
              </button>
            )}

            {["superadmin", "admin", "contributor"].includes(role) && (
              <button
                className={activeTab === "articles" ? "active" : ""}
                onClick={() => setActiveTab("articles")}
              >
                ✍️ Write Article
              </button>
            )}

            {["superadmin", "admin", "contributor"].includes(role) && (
              <button
                className={activeTab === "missions" ? "active" : ""}
                onClick={() => setActiveTab("missions")}
              >
                🎯 Missions
              </button>
            )}

            {["superadmin", "admin", "contributor"].includes(role) && (
              <button
                className={activeTab === "characters" ? "active" : ""}
                onClick={() => setActiveTab("characters")}
              >
                🎭 Characters & Stories
              </button>
            )}

            {["superadmin", "admin"].includes(role) && (
              <button
                className={activeTab === "locations" ? "active" : ""}
                onClick={() => setActiveTab("locations")}
              >
                🗺️ Map Locations
              </button>
            )}

            {["superadmin", "admin"].includes(role) && (
              <button
                className={activeTab === "assets" ? "active" : ""}
                onClick={() => setActiveTab("assets")}
              >
                🛠️ Weapons & Vehicles
              </button>
            )}

              {["superadmin", "admin", "contributor"].includes(role) && (
                <button
                  className={activeTab === "musicradio" ? "active" : ""}
                  onClick={() => setActiveTab("musicradio")}
                >
                  🎵 Music & Radio
                </button>
              )}

              {["superadmin", "admin", "contributor"].includes(role) && (
                <button
                  className={activeTab === "tips" ? "active" : ""}
                  onClick={() => setActiveTab("tips")}
                >
                  💡 Tips & Tricks
                </button>
              )}


          </div>

          {/* Content */}
<div className="profile-content">
  {activeTab === "profile" && userData && (
    <ProfileTab user={userData} role={role} />
  )}

  <ErrorBoundary fallback={<div>Something went wrong in conversations.</div>}>
    {activeTab === "contacts" && role === "superadmin" && (
      <ConversationsTab user={user} role={role} />
    )}
  </ErrorBoundary>

  <ErrorBoundary fallback={<div>Something went wrong in conversations.</div>}>
    {activeTab === "messages" && role !== "superadmin" && (
      <ConversationsTab user={user} role={role} />
    )}
  </ErrorBoundary>

  {activeTab === "users" && role === "superadmin" && <UsersTab allUsers={allUsers} />}

  {activeTab === "articles" && userData && (
    <ArticleTab user={userData} />
  )}

  {activeTab === "missions" && user && (
    <MissionForm user={user} isAdmin={["superadmin","admin","contributor"].includes(role)} enforceAdmin={false} />
  )}

  {activeTab === "characters" && user && (
    <CharacterStoryForm user={user} profile={userData} role={role} />
  )}

  {activeTab === "locations" && user && ["superadmin","admin"].includes(role) && (
    <LocationForm user={user} profile={userData} />
  )}

  {activeTab === "assets" && user && role === "superadmin" && (
    <AssetsTab user={user} profile={userData} isAdmin={role === "superadmin"} enforceAdmin={true} role={role} />
  )}

  {activeTab === "tips" && user && ["superadmin","admin","contributor"].includes(role) && (
    <TipsTab user={user} profile={userData} />
  )}

  {activeTab === "musicradio" && user && ["superadmin","admin","contributor"].includes(role) && (
    <MusicRadioForm user={user} profile={userData} />
  )}
</div>

        </div>
      </PageWrapper>
    </>
  );
};

export default Profile;
